import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { todayIn, type LeaveBalanceDto, type LeaveRequestDto } from '@workforce/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useMyLeaveTypes } from '@/lib/leave';
import { t } from '@/lib/i18n';
import { BalanceCards } from '@/components/BalanceCards';
import { LeaveRequestForm } from '@/components/LeaveRequestForm';
import { LeaveRequestList } from '@/components/LeaveRequestList';
import { Button, Card, ErrorBanner, Modal, Notice, PageHeader, Spinner, Tabs } from '@/components/ui';

export function MyLeavePage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [tab, setTab] = useState<'upcoming' | 'pending' | 'history'>('upcoming');
  const types = useMyLeaveTypes();
  const balances = useQuery({ queryKey: ['me', 'leave', 'balances'], queryFn: () => api.get<LeaveBalanceDto[]>('/me/leave/balances') });
  const requests = useQuery({ queryKey: ['me', 'leave', 'requests'], queryFn: () => api.get<LeaveRequestDto[]>('/me/leave/requests') });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['me', 'leave'] });
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  const today = todayIn(user?.employee?.timezone ?? 'UTC');
  const all = requests.data ?? [];
  const pending = all.filter((r) => r.status === 'pending');
  const upcoming = all.filter((r) => r.status === 'approved' && r.endDate >= today).sort((a, b) => a.startDate.localeCompare(b.startDate));
  const history = all.filter((r) => !(r.status === 'pending' || (r.status === 'approved' && r.endDate >= today)));

  return (
    <div>
      <PageHeader
        title={t('My leave')}
        subtitle={t('Your balances and requests. Weekends and public holidays are never counted.')}
        actions={
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => { setDone(false); setOpen(true); }}>
            {t('Request leave')}
          </Button>
        }
      />
      {done && (
        <div className="mb-4">
          <Notice tone="green">{t("Your request was submitted. You'll get a notification when it is reviewed.")}</Notice>
        </div>
      )}
      <ErrorBanner error={balances.error ?? requests.error} />
      {balances.isLoading ? <Spinner /> : <BalanceCards balances={balances.data ?? []} />}

      <Card className="mt-6" bodyClass="p-0">
        <div className="px-5 pt-4">
          <Tabs
            tabs={[
              { value: 'upcoming', label: t('Upcoming ({n})', { n: upcoming.length }) },
              { value: 'pending', label: t('Pending ({n})', { n: pending.length }) },
              { value: 'history', label: t('Previous ({n})', { n: history.length }) },
            ]}
            value={tab}
            onChange={setTab}
          />
        </div>
        {requests.isLoading ? (
          <Spinner />
        ) : (
          <LeaveRequestList
            items={tab === 'upcoming' ? upcoming : tab === 'pending' ? pending : history}
            showEmployee={false}
            self
            onChanged={refresh}
            empty={tab === 'upcoming' ? t('No upcoming leave') : tab === 'pending' ? t('No pending requests') : t('No previous requests')}
          />
        )}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={t('Request leave')} size="lg">
        {types.data && (
          <LeaveRequestForm
            types={types.data}
            balances={balances.data}
            onCancel={() => setOpen(false)}
            onSubmitted={() => {
              setOpen(false);
              setDone(true);
              setTab('pending');
              refresh();
            }}
          />
        )}
      </Modal>
    </div>
  );
}
